import React, { useEffect, useState } from 'react';
import { Zap, Clock, Users, CheckCircle, ArrowRight, Star, Trophy, Rocket } from 'lucide-react';

interface DemoUsage {
  total_minutes: number;
  session_count: number;
}

export const DemoLimitReached: React.FC = () => {
  const [usage, setUsage] = useState<DemoUsage>({ total_minutes: 0, session_count: 0 });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadUsageStats();
  }, []);

  const loadUsageStats = async () => {
    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/status/check');
      if (response.ok) {
        const data = await response.json();
        setUsage(data.usage || { total_minutes: 0, session_count: 0 });
      }
    } catch (error) {
      console.error('Error loading usage stats:', error);
    }
  };

  const handleSignUp = () => {
    setIsLoading(true);
    window.location.href = 'https://app.customerconnect.live/signup?source=demo_limit&utm_campaign=viral_demo';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl mx-auto">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              You've experienced our platform!
            </h1>
            <p className="text-blue-100">
              Ready to unlock unlimited video sessions?
            </p>
          </div>

          {/* Usage Stats */}
          <div className="px-8 py-6 bg-gray-50 border-b">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
              Your Demo Usage
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-2">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{usage.total_minutes}</div>
                <div className="text-sm text-gray-600">Minutes Used</div>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-2">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{usage.session_count}</div>
                <div className="text-sm text-gray-600">Sessions Created</div>
              </div>
            </div>
          </div>

          {/* Features Unlock */}
          <div className="px-8 py-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
              Unlock Everything with Full Access
            </h3>
            
            <div className="grid gap-4 mb-8">
              <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-gray-900">Unlimited Video Sessions</div>
                  <div className="text-sm text-gray-600">No time limits, no session caps</div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-gray-900">Advanced Features</div>
                  <div className="text-sm text-gray-600">Recording, custom branding, analytics</div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-purple-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-gray-900">Priority Support</div>
                  <div className="text-sm text-gray-600">24/7 support and faster servers</div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-orange-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-gray-900">Team Collaboration</div>
                  <div className="text-sm text-gray-600">Multiple users, shared workspaces</div>
                </div>
              </div>
            </div>

            {/* Social Proof */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-700">
                <div className="flex items-center">
                  <Star className="w-4 h-4 text-yellow-500 mr-1" />
                  <span className="font-semibold">4.9/5</span>
                  <span className="ml-1">rating</span>
                </div>
                <div className="w-1 h-4 bg-gray-300"></div>
                <div className="flex items-center">
                  <Users className="w-4 h-4 text-blue-500 mr-1" />
                  <span className="font-semibold">500+</span>
                  <span className="ml-1">companies</span>
                </div>
                <div className="w-1 h-4 bg-gray-300"></div>
                <div className="flex items-center">
                  <Zap className="w-4 h-4 text-green-500 mr-1" />
                  <span className="font-semibold">99.9%</span>
                  <span className="ml-1">uptime</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              type="button"
              onClick={handleSignUp}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-75 transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                  Setting up your account...
                </>
              ) : (
                <>
                  <Rocket className="w-6 h-6 mr-3" />
                  Get Full Access - Start Free Trial
                  <ArrowRight className="w-6 h-6 ml-3" />
                </>
              )}
            </button>

            <p className="text-xs text-center text-gray-500 mt-4">
              No credit card required • Cancel anytime • 14-day free trial
            </p>
          </div>

          {/* Testimonial */}
          <div className="bg-gray-50 px-8 py-6">
            <div className="text-center">
              <div className="flex justify-center mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-gray-700 italic mb-2">
                "Incredibly easy to use and the video quality is amazing. We switched our entire team over after trying the demo."
              </blockquote>
              <div className="text-sm text-gray-600">
                — Sarah Chen, Marketing Director
              </div>
            </div>
          </div>
        </div>

        {/* Alternative Options */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-4">
            Want to try again tomorrow? Demo limits reset every 24 hours.
          </p>
          <div className="flex justify-center space-x-4">
            <a
              href="/public/demo"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              ← Back to Demo
            </a>
            <span className="text-gray-400">•</span>
            <a
              href="https://app.customerconnect.live"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Learn More
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};